import { signUp } from '@aws-amplify/auth';
import { Box, Button, Divider, Stack, TextField, Typography, useTheme } from '@mui/material';
import { emailRegex } from 'components/Drawer/variants/externalMembers/shared';
import useCurrentTenant from 'hooks/useCurrentTenant/useCurrentTenant';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import PasswordChecklist from 'react-password-checklist';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import { useWizardStepContext } from 'v2/components/WizardModal';
import useGoBackOneOnboardingStep from 'v2/hooks/onboarding/useGoBackOneOnboardingStep/useGoBackOneOnboardingStep';

const STEP_INDEX = 0;
const FORM_ID = 'create-account-cognito-step-form';
const MIN_PASSWORD_LENGTH = 8;

interface FormValues {
  userName: string;
  email: string;
  password: string;
}

const CreateAccountStep = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordValid, setIsPasswordValid] = useState(false);
  const theme = useTheme();
  const { showSnackbar } = useSnackbarContext();
  const handleFetchError = (errorMessage: string) => showSnackbar(errorMessage, { severity: 'error' });
  const { setFooterContent, steps, goToNext, goToPrev } = useWizardStepContext();

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isValid }
  } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: { userName: '', email: '', password: '' }
  });

  const password = watch('password');

  const { getCurrentTenant, currentTenant } = useCurrentTenant({
    handleFetchError
  });
  const { goBackOneOnboardingStep, loading: goBackOneOnboardingStepLoading } = useGoBackOneOnboardingStep({
    handleFetchError
  });

  const onBackButtonClick = async () => {
    if (goBackOneOnboardingStepLoading) return;
    const result = await goBackOneOnboardingStep();
    if (result) {
      goToPrev();
    }
  };

  const onSubmit = async ({ userName, email, password: submittedPassword }: FormValues) => {
    const tenantId = (await getCurrentTenant()).data?.getCurrentTenant.tenantId;
    if (!tenantId) return;
    try {
      setIsLoading(true);
      const data = await signUp({
        username: email,
        password: submittedPassword,
        options: {
          userAttributes: {
            email: email,
            name: userName,
            'custom:tenantId': tenantId
          }
        }
      });

      if (data) {
        setIsLoading(false);
        if (currentTenant?.name) goToNext();

        await getCurrentTenant();
      }
    } catch (err) {
      setIsLoading(false);
      handleFetchError(err instanceof Error ? err.message : String(err));
    }
  };

  React.useLayoutEffect(() => {
    setFooterContent(
      STEP_INDEX,
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column-reverse', md: 'row' }, gap: 2 }}>
        <Button
          key="create-account-cognito-step-back"
          id="create-account-back-button"
          variant="contained"
          type="button"
          color="secondary"
          loading={goBackOneOnboardingStepLoading}
          onClick={() => onBackButtonClick()}
        >
          Back
        </Button>
        <Button
          key="create-account-cognito-step-submit"
          id="create-account-next-button"
          variant="contained"
          type="submit"
          form={FORM_ID}
          disabled={!isValid || !isPasswordValid}
          loading={isLoading}
        >
          Next
        </Button>
      </Box>
    );
  }, [isValid, isPasswordValid, isLoading, goBackOneOnboardingStepLoading, setFooterContent]);

  return (
    <Stack flexDirection="column" gap="16px">
      <Typography variant="h4">Create administrator account</Typography>
      <Divider />
      <Stack
        component="form"
        id={FORM_ID}
        noValidate
        flexDirection="column"
        gap="16px"
        onSubmit={handleSubmit(onSubmit)}
      >
        <TextField
          {...register('userName', { required: 'Please enter a full name' })}
          id="userName-input"
          label="Full name"
          error={!!errors.userName}
          helperText={errors.userName?.message}
        />
        <TextField
          {...register('email', {
            required: 'Please enter an email address',
            validate: (value) => !!value.toLowerCase().match(emailRegex) || 'Please enter a valid email'
          })}
          id="email-input"
          type="email"
          label="Email address"
          error={!!errors.email}
          helperText={errors.email?.message}
        />
        <TextField {...register('password')} id="password-input" type="password" label="Password" />
      </Stack>
      <Typography variant="body2">Password requirements</Typography>
      <Stack flexDirection="column" padding="0 8px" gap="2px">
        <PasswordChecklist
          rules={['minLength', 'specialChar', 'number', 'capital', 'lowercase']}
          minLength={MIN_PASSWORD_LENGTH}
          value={password}
          validColor={theme.palette.success.main}
          invalidColor={theme.palette.error.main}
          onChange={setIsPasswordValid}
          messages={{
            minLength: 'At least 8 characters',
            specialChar: 'A special character',
            number: 'A number',
            capital: 'A capital letter',
            lowercase: 'A lowercase letter'
          }}
        />
      </Stack>
    </Stack>
  );
};

export default CreateAccountStep;
